import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  ShieldCheck, Lock, Flag, ArrowRight, Star, Zap, 
  Play, ChevronRight, Sparkles 
} from 'lucide-react';
import { AuroraBackground } from './AuroraBackground';
import { AnimatedHeadline, GradientText, AnimatedCounter } from './AnimatedText';
import { FloatingElement, GlowOrb, ParticleField } from './FloatingElements';
import { ConfettiButton } from './ConfettiButton';
import { TiltCard } from './TiltCard';

export const HeroSectionUltimate = () => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 400], [1, 0.95]);
  const y = useTransform(scrollY, [0, 400], [0, 100]);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <AuroraBackground className="min-h-screen">
      <section
        id="hero"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden pt-20 lg:pt-24"
      >
        {/* Particle Field */}
        <ParticleField count={30} className="opacity-40" />
        
        {/* Decorative Orbs */}
        <GlowOrb 
          size={400} 
          color="rgba(47, 128, 255, 0.15)" 
          className="-top-40 -right-40" 
        />
        <GlowOrb 
          size={300} 
          color="rgba(45, 212, 191, 0.1)" 
          className="bottom-20 -left-20" 
        />

        {/* Main Content */}
        <motion.div 
          style={{ opacity, scale, y }}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20"
        >
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Text Content */}
            <div className="relative z-10">
              {/* Badge - nur echte Fakten */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 mb-6"
              >
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-300 text-sm font-medium">TSE-zertifiziert | Made in Germany</span>
                </div>
              </motion.div>

              {/* Headline with Animation */}
              {mounted && (
                <AnimatedHeadline
                  text="Das Kassensystem für erfolgreiche Gastronomen"
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1]"
                  delay={0.3}
                  as="h1"
                />
              )}

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                data-testid="hero-subheadline"
                className="mt-6 text-lg sm:text-xl max-w-xl leading-relaxed text-slate-300/90"
              >
                <GradientText from="#2DD4BF" to="#2F80FF">TSE-zertifiziert</GradientText>, 
                finanzamtssicher und kinderleicht zu bedienen. 
                Für Restaurants, Cafés, Bars und Lieferdienste.
              </motion.p>

              {/* Feature Pills */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="mt-6 flex flex-wrap gap-3"
              >
                {[
                  { icon: Zap, text: 'Setup in 5 Min', color: 'amber' },
                  { icon: ShieldCheck, text: 'KassenSichV konform', color: 'emerald' },
                  { icon: Sparkles, text: 'KI-Assistent', color: 'blue' }
                ].map((pill, i) => (
                  <motion.div
                    key={pill.text}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1 + i * 0.1 }}
                    className={`flex items-center gap-2 bg-${pill.color}-500/10 border border-${pill.color}-500/30 rounded-full px-4 py-2`}
                    style={{
                      backgroundColor: pill.color === 'amber' ? 'rgba(245,158,11,0.1)' : 
                                       pill.color === 'emerald' ? 'rgba(16,185,129,0.1)' : 'rgba(59,130,246,0.1)',
                      borderColor: pill.color === 'amber' ? 'rgba(245,158,11,0.3)' : 
                                   pill.color === 'emerald' ? 'rgba(16,185,129,0.3)' : 'rgba(59,130,246,0.3)'
                    }}
                  >
                    <pill.icon className={`w-4 h-4`} style={{
                      color: pill.color === 'amber' ? '#F59E0B' : 
                             pill.color === 'emerald' ? '#10B981' : '#3B82F6'
                    }} />
                    <span className="text-slate-300 text-sm">{pill.text}</span>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="mt-10 flex flex-col sm:flex-row gap-4"
              >
                <ConfettiButton
                  data-testid="hero-primary-cta-button"
                  className="bg-gradient-to-r from-[#2F80FF] to-[#4A92FF] hover:from-[#4A92FF] hover:to-[#60A5FA] text-white px-8 h-14 text-base font-semibold rounded-xl group shadow-lg shadow-blue-500/25 border-0"
                  onClick={() => window.location.href = '/register'}
                >
                  <span className="flex items-center gap-2">
                    14 Tage kostenlos testen
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                </ConfettiButton>
                
                <Button
                  data-testid="hero-secondary-cta-button"
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-white/5 hover:border-slate-500 h-14 px-8 text-base font-medium rounded-xl group"
                  onClick={() => scrollToSection('features')}
                >
                  <Play className="w-5 h-5 mr-2 text-blue-400" />
                  Demo ansehen
                </Button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="mt-12 pt-8 border-t border-slate-800/50"
              >
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Zertifiziert & Sicher</p>
                <div className="flex flex-wrap items-center gap-6">
                  {[
                    { icon: ShieldCheck, title: 'TSE-zertifiziert', sub: 'BSI TR-03153', color: 'emerald' },
                    { icon: Lock, title: 'DSGVO-konform', sub: 'Server in DE', color: 'blue' },
                    { icon: Flag, title: 'Made in Germany', sub: 'Seit 2020', color: 'amber' }
                  ].map((badge) => (
                    <div key={badge.title} className="flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: badge.color === 'emerald' ? 'rgba(16,185,129,0.1)' : 
                                           badge.color === 'blue' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)',
                          border: `1px solid ${badge.color === 'emerald' ? 'rgba(16,185,129,0.3)' : 
                                                badge.color === 'blue' ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.3)'}`
                        }}
                      >
                        <badge.icon className="h-5 w-5" style={{
                          color: badge.color === 'emerald' ? '#10B981' : 
                                 badge.color === 'blue' ? '#3B82F6' : '#F59E0B'
                        }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-200">{badge.title}</p>
                        <p className="text-xs text-slate-500">{badge.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right: Product Showcase */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="relative"
            >
              <TiltCard tiltAmount={8} className="relative">
                {/* Main Screenshot */}
                <div 
                  data-testid="device-mock"
                  className="relative rounded-2xl overflow-hidden"
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 25px 80px -12px rgba(0,0,0,0.6), 0 0 120px -30px rgba(47,128,255,0.4)'
                  }}
                >
                  {/* Browser Chrome */}
                  <div className="bg-[#1a1a2e] px-4 py-3 flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/80" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                      <div className="w-3 h-3 rounded-full bg-green-500/80" />
                    </div>
                    <div className="flex-1 bg-slate-800/50 rounded-lg px-4 py-1.5 text-center">
                      <span className="text-slate-400 text-xs">cash-x.de/kasse</span>
                    </div>
                    {/* Live Badge - in Browser Chrome */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.6 }}
                      className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-3 py-1"
                    >
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-xs text-red-400 font-medium">LIVE</span>
                    </motion.div>
                  </div>
                  
                  {/* Screenshot */}
                  <img 
                    src="/screenshots/dashboard.jpg" 
                    alt="Cash-X Kassensystem Dashboard"
                    className="w-full"
                    loading="eager"
                  />
                </div>
              </TiltCard>

              {/* Floating Stats Card */}
              <FloatingElement duration={4} distance={15} delay={0}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute -bottom-6 -left-6 bg-[#111C32]/90 backdrop-blur-xl border border-slate-700/50 rounded-xl px-5 py-4 shadow-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">+<AnimatedCounter value={23} suffix="%" /></p>
                      <p className="text-sm text-slate-400">mehr Umsatz</p>
                    </div>
                  </div>
                </motion.div>
              </FloatingElement>

              {/* Floating Notification */}
              <FloatingElement duration={5} distance={12} delay={1}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.4 }}
                  className="absolute -top-4 -right-4 bg-[#111C32]/90 backdrop-blur-xl border border-slate-700/50 rounded-xl px-4 py-3 shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-sm text-slate-300">Neue Bestellung #1847</span>
                  </div>
                </motion.div>
              </FloatingElement>
            </motion.div>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
          >
            <span className="text-xs text-slate-500">Scrollen für mehr</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2"
            >
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-1.5 bg-slate-400 rounded-full"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </AuroraBackground>
  );
};

export default HeroSectionUltimate;
